import RBush from "rbush";
import { type Feature, type LineString, type Point } from "geojson";

/**
 * Central, framework-free utilities for repeatedly querying a static
 * LineString:
 *
 *  - `sampleAtDistance` — point at a given distance along the line
 *    (a fast replacement for `@turf/along` in loops / per-frame hot paths).
 *  - `nearest` — closest point on the line to a coordinate
 *    (a fast replacement for `@turf/nearest-point-on-line`).
 *
 * Each line is wrapped in a `LineIndex` that precomputes a cumulative-distance
 * table once. Distance queries then resolve in O(log N) (amortized O(1) for
 * monotonically increasing distances). Nearest queries lazily build an RBush of
 * segment bounding boxes and resolve in roughly O(log N + k).
 *
 * Indexes are cached per geometry object via a WeakMap, so every call site that
 * shares a geometry reference (e.g. `store.trackFeature.geometry`) reuses the
 * same precomputed structure for free, with no lifecycle to manage. This module
 * has no Vue/Pinia dependency and is safe to import from a Web Worker.
 */

const EARTH_RADIUS_KM = 6371.0088; // matches @turf default earthRadius (6371008.8 m)
const DEG_TO_RAD = Math.PI / 180;

export interface LineProjection {
	/** Closest point on the line, as [lng, lat]. */
	coordinates: [number, number];
	/** Distance from the line start to that point, in kilometers. */
	location: number;
	/** Index of the segment's start vertex. */
	segmentIndex: number;
}

export interface NearestResult extends LineProjection {
	/** Distance from the input point to the matched route point, in meters. */
	distanceMeters: number;
}

interface SegmentBox {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
	/** Index of the segment's start vertex. */
	i: number;
}

function haversineKm(ax: number, ay: number, bx: number, by: number): number {
	const dLat = (by - ay) * DEG_TO_RAD;
	const dLon = (bx - ax) * DEG_TO_RAD;
	const lat1 = ay * DEG_TO_RAD;
	const lat2 = by * DEG_TO_RAD;
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
	return 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)) * EARTH_RADIUS_KM;
}

function metersToDegreeRadius(meters: number, latitude: number): number {
	const km = meters / 1000;
	const latDeg = km / 111.32;
	const cosLat = Math.max(Math.cos(latitude * DEG_TO_RAD), 0.01);
	const lngDeg = km / (111.32 * cosLat);
	return Math.max(latDeg, lngDeg);
}

/** Smallest index in [lo, hi] with arr[index] >= target. Assumes such an index exists. */
function lowerBound(arr: Float64Array, target: number, lo: number, hi: number): number {
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if (arr[mid] < target) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

/**
 * Precomputed acceleration structure over a single static LineString.
 * Build via {@link getLineIndex} (cached) or {@link createLineIndex} (uncached).
 */
export class LineIndex {
	private readonly coords: number[][];
	private readonly n: number;
	/** cumulative[i] = distance (km) from the start to vertex i. */
	private readonly cumulative: Float64Array;
	/** Total line length, in kilometers. */
	readonly length: number;
	/** Cached lower-bound vertex from the previous distance query (forward-scan fast path). */
	private cachedIndex = 0;
	/** Lazily built segment index for nearest-point queries. */
	private tree: RBush<SegmentBox> | undefined;

	constructor(geometry: LineString) {
		const coords = geometry.coordinates;
		const n = coords.length;
		const cumulative = new Float64Array(Math.max(n, 1));
		for (let i = 1; i < n; i++) {
			cumulative[i] = cumulative[i - 1] + haversineKm(coords[i - 1][0], coords[i - 1][1], coords[i][0], coords[i][1]);
		}
		this.coords = coords;
		this.n = n;
		this.cumulative = cumulative;
		this.length = n > 0 ? cumulative[n - 1] : 0;
	}

	/** Cumulative distance (km) from the line start to vertex `i` (clamped to range). */
	distanceAtVertex(i: number): number {
		if (this.n === 0) return 0;
		const clamped = i < 0 ? 0 : i > this.n - 1 ? this.n - 1 : i;
		return this.cumulative[clamped];
	}

	/** Point at `distanceKm` along the line, as [lng, lat]. */
	sampleAtDistance(distanceKm: number): [number, number] {
		const { coords, n, cumulative } = this;
		if (n === 0) return [0, 0];
		if (n === 1 || distanceKm <= 0) return coords[0] as [number, number];
		if (distanceKm >= this.length) return coords[n - 1] as [number, number];

		// Find the smallest i with cumulative[i] >= distanceKm (segment [i-1, i]).
		let i: number;
		if (cumulative[this.cachedIndex] <= distanceKm) {
			i = Math.max(this.cachedIndex, 1);
			if (cumulative[i] < distanceKm) {
				i = lowerBound(cumulative, distanceKm, i, n - 1);
			} else {
				while (cumulative[i] < distanceKm) i++;
			}
		} else {
			i = lowerBound(cumulative, distanceKm, 1, this.cachedIndex);
		}
		this.cachedIndex = i;

		const segStart = cumulative[i - 1];
		const segLength = cumulative[i] - segStart;
		const t = segLength > 0 ? (distanceKm - segStart) / segLength : 0;
		const a = coords[i - 1];
		const b = coords[i];
		return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
	}

	/** Point at `distanceKm` along the line, as a GeoJSON Point feature (drop-in for `@turf/along`). */
	sampleFeatureAtDistance(distanceKm: number): Feature<Point> {
		return {
			type: "Feature",
			properties: {},
			geometry: { type: "Point", coordinates: this.sampleAtDistance(distanceKm) },
		};
	}

	private getTree(): RBush<SegmentBox> {
		if (this.tree) return this.tree;
		const { coords, n, cumulative } = this;
		const boxes: SegmentBox[] = [];
		for (let i = 1; i < n; i++) {
			const a = coords[i - 1];
			const b = coords[i];
			// guard against accidental cumulative drift on rebuild
			if (cumulative[i] === 0 && i > 0 && cumulative[i - 1] > 0) {
				cumulative[i] = cumulative[i - 1] + haversineKm(a[0], a[1], b[0], b[1]);
			}
			boxes.push({
				minX: Math.min(a[0], b[0]),
				minY: Math.min(a[1], b[1]),
				maxX: Math.max(a[0], b[0]),
				maxY: Math.max(a[1], b[1]),
				i: i - 1,
			});
		}
		const tree = new RBush<SegmentBox>();
		tree.load(boxes);
		this.tree = tree;
		return tree;
	}

	private evaluateAll(candidates: SegmentBox[], px: number, py: number): LineProjection[] {
		const { coords } = this;
		const results: LineProjection[] = [];

		for (const box of candidates) {
			const i = box.i;
			const a = coords[i];
			const b = coords[i + 1];
			const lngScale = Math.max(Math.cos(py * DEG_TO_RAD), 0.01);
			const dx = (b[0] - a[0]) * lngScale, dy = b[1] - a[1];
			const lenSq = dx * dx + dy * dy;
			let t = lenSq > 0 ? ((px - a[0]) * lngScale * dx + (py - a[1]) * dy) / lenSq : 0;
			if (t < 0) t = 0;
			else if (t > 1) t = 1;
			const cx = a[0] + (b[0] - a[0]) * t;
			const cy = a[1] + dy * t;
			const segLen = this.cumulative[i + 1] - this.cumulative[i];
			results.push({
				coordinates: [cx, cy],
				location: this.cumulative[i] + segLen * t,
				segmentIndex: i,
			});
		}

		return results;
	}

	/** Evaluate the closest point among a set of candidate segments. */
	private evaluate(candidates: SegmentBox[], px: number, py: number): LineProjection | undefined {
		let best: LineProjection | undefined;
		let bestDistSq = Infinity;
		const lngScale = Math.max(Math.cos(py * DEG_TO_RAD), 0.01);
		for (const result of this.evaluateAll(candidates, px, py)) {
			const ddx = (px - result.coordinates[0]) * lngScale, ddy = py - result.coordinates[1];
			const distSq = ddx * ddx + ddy * ddy;
			if (distSq < bestDistSq) {
				bestDistSq = distSq;
				best = result;
			}
		}
		return best;
	}

	/** All candidate projections whose segment bboxes intersect a degree radius. */
	nearbyDegreeWindow(coords: [number, number], maxRadiusDeg: number): LineProjection[] {
		if (this.n < 2) return [];

		const tree = this.getTree();
		const [px, py] = coords;
		const candidates = tree.search({
			minX: px - maxRadiusDeg,
			minY: py - maxRadiusDeg,
			maxX: px + maxRadiusDeg,
			maxY: py + maxRadiusDeg,
		});

		return this.evaluateAll(candidates, px, py);
	}

	/**
	 * All route passes within `maxRadiusMeters` of `coords`.
	 *
	 * This is intended for loop-aware marker placement: callers can then filter
	 * by relative distance and route-distance separation to decide which hits are
	 * distinct passings of the same marker.
	 */
	nearby(coords: [number, number], maxRadiusMeters: number): NearestResult[] {
		if (this.n < 2) return [];

		const tree = this.getTree();
		const [px, py] = coords;
		const radiusDeg = metersToDegreeRadius(maxRadiusMeters, py);
		const candidates = tree.search({
			minX: px - radiusDeg,
			minY: py - radiusDeg,
			maxX: px + radiusDeg,
			maxY: py + radiusDeg,
		});
		const maxRadiusKm = maxRadiusMeters / 1000;
		const matches: NearestResult[] = [];

		for (const projection of this.evaluateAll(candidates, px, py)) {
			const distanceKm = haversineKm(px, py, projection.coordinates[0], projection.coordinates[1]);
			if (distanceKm > maxRadiusKm) continue;

			matches.push({
				...projection,
				distanceMeters: distanceKm * 1000,
			});
		}

		return matches;
	}

	/**
	 * Closest point on the line to `coords`.
	 *
	 * When `maxRadiusDeg` is given, only segments within that degree window are
	 * considered and `undefined` is returned if none qualify — ideal for the
	 * mouse-hover hot path where far-away misses should bail out cheaply.
	 *
	 * When omitted, performs an exact nearest search (expanding window + a guard
	 * pass) suitable for one-off marker snapping where a result is always wanted.
	 *
	 * Distances use a planar (degree) metric, which is accurate enough to select
	 * the correct segment at the scales used here; callers needing exact ground
	 * distance can recompute from the returned coordinates.
	 */
	nearest(coords: [number, number], maxRadiusDeg?: number): LineProjection | undefined {
		if (this.n < 2) return undefined;
		const tree = this.getTree();
		const [px, py] = coords;

		if (maxRadiusDeg !== undefined) {
			const candidates = tree.search({
				minX: px - maxRadiusDeg,
				minY: py - maxRadiusDeg,
				maxX: px + maxRadiusDeg,
				maxY: py + maxRadiusDeg,
			});
			if (candidates.length === 0) return undefined;
			return this.evaluate(candidates, px, py);
		}

		// Unbounded exact search: expand the window until we find candidates,
		// then run one guard query sized to the best distance so no closer
		// segment (whose bbox could lie just outside the first window) is missed.
		let radius = this.estimateInitialRadius();
		let candidates: SegmentBox[] = [];
		for (let attempt = 0; attempt < 32 && candidates.length === 0; attempt++) {
			candidates = tree.search({ minX: px - radius, minY: py - radius, maxX: px + radius, maxY: py + radius });
			if (candidates.length === 0) radius *= 2;
		}
		if (candidates.length === 0) {
			// Degenerate fallback: scan everything.
			candidates = tree.all();
		}
		const best = this.evaluate(candidates, px, py);
		if (!best) return undefined;

		const bestDist = Math.hypot(best.coordinates[0] - px, best.coordinates[1] - py);
		const guard = tree.search({
			minX: px - bestDist, minY: py - bestDist, maxX: px + bestDist, maxY: py + bestDist,
		});
		return guard.length > candidates.length ? this.evaluate(guard, px, py) : best;
	}

	private estimateInitialRadius(): number {
		// A coarse first guess: average segment span keeps the first query small
		// for dense lines while staying generous for sparse ones.
		if (this.n < 2) return 0.01;
		const span = Math.max(this.length / Math.max(this.n - 1, 1), 1e-4);
		// length is km; ~111 km per degree.
		return Math.max(span / 111, 1e-4);
	}
}

const cache = new WeakMap<LineString, LineIndex>();

/** Get a cached {@link LineIndex} for a geometry, building it on first use. */
export function getLineIndex(geometry: LineString): LineIndex {
	let index = cache.get(geometry);
	if (!index) {
		index = new LineIndex(geometry);
		cache.set(geometry, index);
	}
	return index;
}

/** Build an uncached {@link LineIndex} (e.g. inside a Web Worker). */
export function createLineIndex(geometry: LineString): LineIndex {
	return new LineIndex(geometry);
}
